/**
 * Villain deck reveal move for the Legendary Arena game engine.
 *
 * revealVillainCard draws the top card from the villain deck, looks up its
 * classification in G.villainDeckCardTypes, emits the appropriate rule
 * triggers via the WP-009B pipeline, applies the resulting effects, and
 * places the card in discard.
 *
 * This move assumes the deck already exists in G. It does not construct
 * or validate deck composition — that is WP-014B's responsibility.
 *
 * No registry imports. No .reduce(). Moves never throw.
 */
import { formatCardRef } from '../log/logDisplay.js';
import { executeRuleHooks } from '../rules/ruleRuntime.execute.js';
import { applyRuleEffects } from '../rules/ruleRuntime.effects.js';
import { DEFAULT_IMPLEMENTATION_MAP } from '../rules/ruleRuntime.impl.js';
import { pushVillainIntoCity } from '../board/city.logic.js';
import { validateCityShape } from '../board/city.validate.js';
import { ENDGAME_CONDITIONS } from '../endgame/endgame.types.js';
import { carryEscapedBystandersToPile } from '../board/bystanders.logic.js';
import { applyEscapedPileResourceLoss } from '../rules/schemeResourceLoss.js';
// why: WP-832 / D-24765 — a function-level cycle (the WWII invasion plays cards through this module).
import { settleWwiiConquest } from '../rules/changeTheOutcomeOfWwii.logic.js';
import { hasAmbush } from '../board/boardKeywords.logic.js';
import { moveAttachedHeroesToEscapedPile } from '../board/heroCapture.logic.js';
// why: WP-793 / D-24656 — the escape records the rulebook HQ KO + Bystander discard it owes.
// villainEscapeProcedure → phaseCard → playVillainTop.resolve → this module is a
// runtime-safe import cycle: enqueueEscapeProcedure is referenced only inside a function
// body, never at module top level, so ESM resolves the binding by call time (the
// divingBlock.logic.ts precedent).
import { enqueueEscapeProcedure } from './villainEscapeProcedure.js';
import { recordEffectTrace } from '../diagnostics/effectTrace.record.js';
import { MASTER_STRIKE_THIS_TURN_CONDITION_TYPE, MASTER_STRIKE_OR_AMBUSH_THIS_TURN_CONDITION_TYPE, matchReadsConditionType, } from '../hero/heroConditions.evaluate.js';
import { executeVillainAbilities, resolveEffectResultNames, villainCardEscapeTriggersSchemeTwist, villainCardPlaysVillainDeckCards, } from '../villain/villainEffects.execute.js';
import { hasPendingKoHeroChoice } from '../moves/koHeroChoice.resolve.js';
import { hasPendingScryKoChoice } from '../moves/scryKoChoice.resolve.js';
import { hasPendingMelterKoChoice } from '../moves/melterKoChoice.resolve.js';
import { hasPendingRuthlessDictatorChoice } from '../moves/ruthlessDictatorChoice.resolve.js';
import { hasPendingElectromagneticBubbleChoice } from '../moves/electromagneticBubbleChoice.resolve.js';
import { hasPendingDiscardChoice } from '../moves/discardChoice.resolve.js';
import { hasPendingPutCardsOnDeckChoice } from '../moves/putCardsOnDeckChoice.resolve.js';
import { hasPendingKoDiscardChoice } from '../moves/koDiscardChoice.resolve.js';
import { hasPendingReorderChoice } from '../moves/reorderChoice.resolve.js';
import { hasPendingDefeatChoice } from '../moves/defeatChoice.resolve.js';
import { hasPendingOptionalKoReward } from '../moves/optionalKoReward.resolve.js';
import { hasPendingSmashDiscard } from '../moves/smashDiscard.resolve.js';
import { hasPendingPutHandOnDeckTop } from '../moves/putHandOnDeckTop.resolve.js';
import { hasPendingRevealTopDispose } from '../moves/revealTopDispose.resolve.js';
import { hasPendingRevealThreeAssign } from '../moves/revealThreeAssign.resolve.js';
import { hasPendingDoOver } from '../moves/doOver.resolve.js';
import { hasPendingPlayVillainTopChoice } from '../moves/playVillainTop.resolve.js';
import { hasPendingVictoryPileCardPick } from '../moves/resolveVictoryPileCardPick.js';
import { hasPendingDrawOrEmpowered } from '../moves/drawOrEmpowered.resolve.js';
import { hasPendingCoveringFireChoice } from '../moves/coveringFireChoice.resolve.js';
import { hasPendingMoveVillainChoice } from '../moves/moveVillainChoice.resolve.js';
import { hasPendingSplitFaceChoice } from '../moves/splitFaceChoice.resolve.js';
import { hasPendingCountScaledChoice } from '../moves/countScaledChoice.resolve.js';
import { hasPendingUndercoverChoice } from '../moves/undercover.resolve.js';
import { hasPendingSeatChoice } from '../moves/seatChoice.resolve.js';
import { hasPendingReturnZeroCostDiscard } from '../moves/resolveReturnZeroCostDiscard.js';
import { composeAmbushNarrative, composeBystanderRevealedNarrative, composeEffectResultLogLine, composeVillainDeckPlayClause, } from '../events/notableEvents.compose.js';
import { pushLog } from '../log/logPush.js';
/**
 * Reads the boardgame.io turn number off a `RevealContext`, defaulting to 0.
 *
 * Every production site that builds a `RevealContext` forwards the framework turn
 * (D-24704), so the WP-488 `secondary-site` trace stamps the same `turn` as the
 * villain-executor traces on this path, which read it off the same `ctx`. A hand-built
 * test context with no numeric `turn` falls back to 0. Never throws.
 *
 * @param context - The reveal context (its `ctx` fragment is read for `turn`).
 * @returns The turn number, or 0 when unavailable.
 */
function readRevealContextTurn(context) {
    const turn = context.ctx.turn;
    if (typeof turn === 'number' && Number.isFinite(turn)) {
        return turn;
    }
    return 0;
}
/**
 * Reveals the top card from the villain deck (boardgame.io move wrapper).
 *
 * Thin wrapper around performVillainReveal. The wrapper applies the
 * start-stage gate; performVillainReveal owns the draw → classify → route →
 * trigger → apply pipeline. Splitting the gate from the pipeline lets rule
 * handlers (e.g., Midtown Bank Robbery twist) chain another reveal without
 * re-asserting the stage gate or duplicating the body.
 *
 * @param context - boardgame.io move context with G, ctx, random, playerID.
 */
export function revealVillainCard({ G, ctx, ...context }) {
    // Step 0: Stage gate (non-core move contract)
    // why: villain reveal is a start-of-turn action per tabletop Legendary
    if (G.currentStage !== 'start')
        return;
    // why: block-all guard (D-24008) — while a KO-a-Hero choice is pending the
    // board is frozen; revealVillainCard returns with no side effects. Placed
    // immediately after the stage gate, before the once-per-turn guard and any
    // G/zone write.
    if (hasPendingKoHeroChoice(G))
        return;
    // why: block-all guard (D-24282) — a pending Doombot scry-KO choice freezes the
    // board until the player picks which revealed card to KO.
    if (hasPendingScryKoChoice(G))
        return;
    // why: WP-603 / D-24413 — block-all guard parity: a pending Melter Fight KO/keep
    // choice freezes the board. Practically unreachable here (a parked choice already
    // blocks advanceStage, so no next-turn reveal fires mid-choice), but guarded
    // alongside scry-ko so the "every site or none" invariant holds.
    if (hasPendingMelterKoChoice(G))
        return;
    // why: WP-695 / D-24512 — block-all guards for the two interactive core mastermind
    // tactics (Ruthless Dictator scry-3 / Electromagnetic Bubble X-Men pick).
    if (hasPendingRuthlessDictatorChoice(G))
        return;
    if (hasPendingElectromagneticBubbleChoice(G))
        return;
    // why: block-all guard (WP-476 / D-24284) — a pending discard-to-limit choice
    // (parked by a Magneto strike at THIS start stage) freezes the board until the
    // current player picks which cards to discard; blocks re-reveal until resolved.
    if (hasPendingDiscardChoice(G))
        return;
    if (hasPendingPutCardsOnDeckChoice(G))
        return; // why: WP-538 / D-24347 block-all guard
    if (hasPendingReorderChoice(G))
        return; // why: WP-479 / D-24286 block-all guard
    if (hasPendingDefeatChoice(G))
        return; // why: WP-486 / D-24291 block-all guard
    if (hasPendingKoDiscardChoice(G))
        return; // why: WP-693 / D-24510 block-all guard
    // why: block-all guard (D-24019) — optional-KO-reward choice pending; the
    // board is frozen until resolved (beside the D-24008 KO-hero check above).
    if (hasPendingPlayVillainTopChoice(G))
        return; // why: WP-663 / D-24474 — block-all guard (Shadowed Thoughts play-villain-top choice)
    if (hasPendingOptionalKoReward(G))
        return;
    if (hasPendingSmashDiscard(G))
        return; // why: WP-676 / D-24492 — block-all guard (Smash discard-for-attack choice)
    if (hasPendingPutHandOnDeckTop(G))
        return; // why: WP-700 / D-24519 — block-all guard (put-a-hand-card-on-deck-top choice)
    if (hasPendingRevealTopDispose(G))
        return; // why: WP-702 / D-24521 — block-all guard (reveal-top discard-or-keep choice)
    if (hasPendingRevealThreeAssign(G))
        return; // why: WP-753 / D-24580 — block-all guard (reveal-three draw / discard / KO assignment)
    if (hasPendingDoOver(G))
        return; // why: WP-681 / D-24498 — block-all guard (Do-Over accept/decline choice)
    // why: block-all — pendingVictoryPileCardPick must be resolved before any other action (D-24067)
    if (hasPendingVictoryPileCardPick(G))
        return;
    // why: block-all — pendingDrawOrEmpowered must be resolved before any other action (D-24069)
    if (hasPendingDrawOrEmpowered(G))
        return;
    // why: block-all guard (WP-719 / D-24541) — a pending Covering Fire choice freezes the board.
    if (hasPendingCoveringFireChoice(G))
        return;
    // why: block-all guard (WP-795 / D-24664) — a pending Spinning Cyclone move-a-Villain choice freezes the board.
    if (hasPendingMoveVillainChoice(G))
        return;
    // why: block-all guard (WP-724 / D-24546) — a pending split-face "choose a side" freezes the board.
    if (hasPendingSplitFaceChoice(G))
        return;
    if (hasPendingCountScaledChoice(G))
        return;
    // why: block-all — pendingUndercoverChoice must be resolved before any other action (WP-678 / D-24494)
    if (hasPendingUndercoverChoice(G))
        return;
    if (hasPendingSeatChoice(G))
        return; // why: WP-684 / D-24501 — block-all (non-active/multi-seat pending choice)
    // why: block-all — pendingReturnZeroCostDiscard must be resolved before any other action (D-24139)
    if (hasPendingReturnZeroCostDiscard(G))
        return;
    // why: the start-of-turn reveal is once per turn; scheme/card effects that
    // chain extra reveals call performVillainReveal directly and intentionally
    // bypass this guard.
    if (G.villainRevealedThisTurn)
        return;
    performVillainReveal(G, { random: context.random, ctx: { currentPlayer: ctx.currentPlayer, turn: ctx.turn } }, DEFAULT_IMPLEMENTATION_MAP);
    // why: the player's single start-of-turn reveal attempt is now consumed; set
    // regardless of whether the call above mutated the board (an exhausted-deck
    // no-op still spends the allowance, foreclosing a same-turn retry loop).
    G.villainRevealedThisTurn = true;
}
/**
 * Reveals the top card from the villain deck and runs the full trigger pipeline.
 *
 * Pipeline: draw → classify → City routing (villain/henchman) → trigger →
 * apply effects → discard routing (bystander/scheme-twist/mastermind-strike).
 *
 * Handles edge cases:
 * - Empty deck + non-empty discard: reshuffles discard into deck first.
 * - Empty deck + empty discard: logs a message and returns.
 * - Missing card type: fail-closed — logs a message, card stays in deck.
 *
 * @param G - The game state to mutate.
 * @param context - Narrow context with random + ctx.currentPlayer.
 * @param implementationMap - Handler map used by executeRuleHooks.
 */
export function performVillainReveal(G, context, implementationMap) {
    const ctx = context.ctx;
    const deck = G.villainDeck.deck;
    // Step 1: Handle empty deck
    // why: WP-367 / D-24160 — the Villain Deck does NOT reshuffle from its discard.
    // Per the quoted rulebook the deck running out is terminal: it latches the
    // final turn (see game.ts turn.onMove, D-24159) rather than being refilled.
    // (In practice the villain discard is already dead: revealed villains/henchmen
    // route to the City and other card types route to their own piles, so nothing
    // accumulates in villainDeck.discard to reshuffle anyway.) An empty deck here
    // is simply a no-op reveal — the latch was already set on the reveal that drew
    // the last card.
    if (deck.length === 0) {
        pushLog(G, 'Villain deck reveal skipped: the villain deck is empty.');
        return;
    }
    // Step 2: Draw the top card (top-of-deck = deck[0], locked convention)
    const cardId = G.villainDeck.deck[0];
    if (!cardId) {
        pushLog(G, 'Villain deck reveal skipped: the villain deck is empty.');
        return;
    }
    // Step 3: Look up classification — fail-closed if missing
    const cardType = G.villainDeckCardTypes[cardId];
    if (!cardType) {
        pushLog(G, `Villain deck reveal failed: card "${cardId}" has no entry in villainDeckCardTypes. No removal or trigger occurred.`);
        return;
    }
    // Step 4: City routing for villain and henchman cards
    // why: City placement before triggers so hooks observe post-placement state.
    // This ordering is contractual — rule hooks see the physical board state that
    // players would see immediately after a reveal (Legendary tabletop semantics).
    // why: Deck removal is deferred until placement destination is confirmed.
    // If city validation fails, the card must remain on top of the deck — removing
    // it before validation would silently lose the card (WP-015A fix).
    if (cardType === 'villain' || cardType === 'henchman') {
        const cityValidation = validateCityShape(G.city);
        if (!cityValidation.ok) {
            pushLog(G, `Villain city placement skipped: G.city is malformed. Card "${cardId}" remains in deck.`);
            return;
        }
        // Remove card from deck only after city validation succeeds
        G.villainDeck.deck = G.villainDeck.deck.slice(1);
        const pushResult = pushVillainIntoCity(G.city, cardId);
        G.city = pushResult.city;
        // why: every villain-deck reveal must produce a start-stage log line. The
        // bystander branch logs "revealed and captured by X" and mastermind-strike
        // logs via its rule hook, but a villain/henchman entering the city had no
        // base log line — so the first turn (empty city → the reveal fills it, no
        // escape) showed nothing at step 1 and the game log appeared to start at
        // step 2. Log the city entry here, before the escape/wound/ambush detail
        // lines below, so the reveal is always narrated. (G.messages is
        // hash-excluded, D-24081, so this is replay-safe.)
        // why: WP-643-follow-up — an Ambush card flags the entry line `[Ambush]` and
        // colours it `threat` (villain-purple) so a player sees the incoming threat
        // stand out; the printed effect targets are named on the coloured
        // "Ambush effect:" line below (fired after the executor resolves them). The
        // "revealed and entered the city" substring is kept in BOTH branches so the
        // city-entry log assertions stay valid regardless of Ambush. hasAmbush is
        // resolved once here and reused at the Ambush executor site below.
        const cardHasAmbush = hasAmbush(cardId, G.cardKeywords ?? {});
        if (cardHasAmbush) {
            pushLog(G, `[Ambush] ${formatCardRef(G.cardDisplayData, cardId)} revealed and entered the city!`, 'threat');
        }
        else {
            pushLog(G, `${formatCardRef(G.cardDisplayData, cardId)} revealed and entered the city.`);
        }
        if (pushResult.escapedCard !== null) {
            // why: WP-757 / D-24587 — the escape branch is extracted MECHANICALLY into
            // resolveVillainEscape so a no-Ambush City entry (the Haunt exorcise,
            // enterCityIgnoringAmbush) resolves an escape with exact reveal parity.
            resolveVillainEscape(G, context, implementationMap, pushResult.escapedCard);
        }
        // why: Ambush fires on City entry. The hardcoded "each player gains a
        // wound" loop previously here is deleted (D-18504; supersedes the D-2403
        // safe-skip note for the Ambush case) — it fired identical wrong behavior
        // for every Ambush card regardless of printed text. Dispatch now runs the
        // card's parsed [effect:] hooks via executeVillainAbilities, gated by the
        // existing hasAmbush fast pre-check (the keyword-detection invariant from
        // buildCardKeywords.ts). The keyword map is re-derived inline so this call
        // carries no dependency on a deleted binding.
        // why: WP-200 — capture the executor's return and emit `ambushResolved`.
        // Only genuine Ambush `[effect:]` results feed `appliedEffects` / the
        // narrative. (The MVP unconditional city-entry bystander attach — D-1701,
        // NOT the D-18504 ambush-wound deletion above — was removed by WP-432 as
        // non-canonical, so there is no longer a non-Ambush attach to exclude here.)
        // Resolving the citySpace via `G.city.indexOf(cardId)`
        // after `pushVillainIntoCity` reflects the final placement index
        // (0..4); -1 falls back to 0 if the push collapsed the card off the
        // edge (a contract violation the move never hits in production but
        // the emission must remain defensive).
        if (cardHasAmbush) {
            // why: WP-743 / D-24566 — record the Ambush Villain for Spring the Trap's
            // this-turn gate. Written only when a hero hook in this match reads that
            // condition (games without it stay byte-unchanged, the D-24467 posture), and
            // BEFORE The Leader's chained play below, so a card that chain reveals sees it.
            if (matchReadsConditionType(G, MASTER_STRIKE_OR_AMBUSH_THIS_TURN_CONDITION_TYPE)) {
                G.ambushVillainPlayedThisTurn = true;
            }
            // why: WP-478 / D-24285 — pass the reveal move's shuffle source (uniform with
            // the Fight/Escape fire sites) so a future Ambush-timed scry could reshuffle.
            // why: WP-489 / D-24295 — the Ambush fire site has no fought City space, so
            // cityIndex is undefined; the location gate FAILS CLOSED (no Tier-B card ambushes).
            const appliedAmbushResults = executeVillainAbilities(G, ctx, cardId, 'onAmbush', { random: context.random }, undefined);
            // why: WP-316 — map results→keywords so the ambushResolved `appliedEffects`
            // field and the composeAmbushNarrative string stay byte-identical to main;
            // the hashed notableEvents surface (and the arena-client) never observe the
            // per-target widening, so finalStateHash is unchanged.
            const appliedAmbushEffects = appliedAmbushResults.map((result) => result.keyword);
            // why: WP-200 — defensive access; legacy test states may leave
            // `cardDisplayData` undefined. Production setup always builds it.
            const ambushCardDisplay = G.cardDisplayData?.[cardId];
            const ambushCardName = ambushCardDisplay && typeof ambushCardDisplay.name === 'string' && ambushCardDisplay.name.length > 0
                ? ambushCardDisplay.name
                : cardId;
            const ambushCitySpace = (() => {
                const found = G.city.indexOf(cardId);
                return found >= 0 ? found : 0;
            })();
            // why: WP-316 + WP-319 — resolve the effect targets to display names ONCE;
            // the same resolved results feed BOTH the durable `Ambush effect:` log line
            // AND the ambushResolved narrative (so the overlay + log name the same hero).
            const resolvedAmbushResults = resolveEffectResultNames(G, appliedAmbushResults);
            // why: WP-316 — narrate the Ambush: effect targets into the durable log
            // (G.messages, hash-excluded per D-24081) after the executor runs and
            // BEFORE the ambushResolved event push. Length-guarded: no line when no
            // effect applied. The unconditional city-entry bystander attach below is
            // NOT an Ambush effect and never appears here.
            if (appliedAmbushResults.length > 0) {
                // why: WP-643-follow-up — colour the Ambush effect line `threat`
                // (villain-purple), matching the `[Ambush]` entry line above, so the whole
                // Ambush reads as one board-adversity beat in the log.
                pushLog(G, `Ambush effect: ${composeEffectResultLogLine(resolvedAmbushResults)}.`, 'threat');
            }
            // why: D-24703 — The Leader's Ambush play (WP-542 / D-24351) is performed below,
            // not by the executor, so read its count BEFORE the ambushResolved push and feed it
            // to the narrative; otherwise the overlay reads "entered the city with no Ambush
            // effect" while the Ambush plays a card. The narrated log line mirrors the Fight
            // site's, so the played card's reveal is attributed to this villain. The play
            // itself still fires AFTER the event (ordering unchanged).
            // why: WP-837 / D-24770 — the current player decides a reveal-gated play (The Leader).
            const ambushPlayCount = villainCardPlaysVillainDeckCards(G, cardId, 'onAmbush', ctx.currentPlayer);
            if (ambushPlayCount > 0) {
                pushLog(G, `Ambush effect: ${formatCardRef(G.cardDisplayData, cardId)} ${composeVillainDeckPlayClause(ambushPlayCount)}.`, 'threat');
            }
            G.notableEvents.push({
                type: 'ambushResolved',
                revealedCardId: cardId,
                citySpace: ambushCitySpace,
                appliedEffects: appliedAmbushEffects,
                // why: WP-319 — the narrative now names the captured HQ hero / KO'd hero
                // (etc.) via the resolved results, so the center-screen overlay announces
                // the specific hero. `appliedEffects` stays the keyword array (badges).
                narrative: composeAmbushNarrative(ambushCardName, resolvedAmbushResults, ambushPlayCount),
            });
            // why: WP-542 / D-24351 — The Leader's "Ambush: Play the top card of the Villain
            // Deck." The executor's play-villain-deck-cards handler is a no-op (it cannot reach
            // the reveal pipeline); the actual play fires HERE, where the RevealContext + the
            // implementationMap are in scope. Fired AFTER the ambush abilities resolve (the
            // ambushResolved event above) — the Ambush plays the top N villain-deck cards, each
            // resolving as a normal reveal (villain→city, henchman / bystander / scheme-twist /
            // master-strike, recursing through the rule pipeline). N is the marked count.
            if (ambushPlayCount > 0) {
                playTopVillainDeckCards(G, context, implementationMap, ambushPlayCount);
            }
        }
        // why: WP-432 (supersedes D-1701 / removes the WP-431 entry-capture log) —
        // a villain/henchman does NOT capture a bystander merely by entering the
        // City. Canonical Legendary: bystanders enter play ONLY via a bystander CARD
        // revealed from the villain deck (captured by the city villain closest to the Villain Deck, or
        // the Mastermind if the city is empty — the `cardType === 'bystander'` branch
        // below) or via a specific Ambush / Master-Strike / Scheme-Twist / Fight
        // `capture-bystander` effect. The former MVP unconditional attach from
        // `G.piles.bystanders` (D-1701 / WP-017) was a second, non-canonical source
        // that doubled bystanders-in-play and drained the supply pile that hero
        // "Rescue a Bystander" abilities share (D-24032 floor). It is deleted here.
    }
    else {
        // Non-city card types: remove from deck before trigger/discard routing
        G.villainDeck.deck = G.villainDeck.deck.slice(1);
    }
    // Step 5: Collect rule effects via the WP-009B pipeline
    // why: pass the full RevealContext (not just ctx) to executeRuleHooks /
    // applyRuleEffects so handlers/applicators that need `random` (e.g., the
    // Midtown Bank Robbery twist chaining another reveal, or the drawCards
    // effect's reshuffle path) can reach it via `context.random.Shuffle`.
    const allEffects = [];
    // Always emit onCardRevealed
    const cardRevealedEffects = executeRuleHooks(G, context, 'onCardRevealed', { cardId, cardTypeSlug: cardType }, G.hookRegistry, implementationMap);
    for (const effect of cardRevealedEffects) {
        allEffects.push(effect);
    }
    // Conditionally emit type-specific triggers
    if (cardType === 'scheme-twist') {
        const schemeTwistEffects = executeRuleHooks(G, context, 'onSchemeTwistRevealed', { cardId }, G.hookRegistry, implementationMap);
        for (const effect of schemeTwistEffects) {
            allEffects.push(effect);
        }
    }
    if (cardType === 'mastermind-strike') {
        const mastermindStrikeEffects = executeRuleHooks(G, context, 'onMastermindStrikeRevealed', { cardId }, G.hookRegistry, implementationMap);
        for (const effect of mastermindStrikeEffects) {
            allEffects.push(effect);
        }
    }
    // Step 6: Apply all collected effects
    applyRuleEffects(G, context, allEffects);
    // Step 7: Route card to final destination based on type
    // Villain and henchman cards are already in the City (step 4b above).
    // All other card types go to discard.
    if (cardType === 'villain' || cardType === 'henchman') {
        // Already placed in City in step 4b — do not also place in discard
    }
    else if (cardType === 'bystander') {
        // why: WP-747 / D-24571 — Universal Rules v23 (L606-608): "Put the Bystander
        // under the Villain/Adversary in the city that's closest to the
        // Villain/Adversary Deck." Villains enter at index 0 (the Villain Deck side;
        // pushVillainIntoCity) and escape from index 4, so the captor is the LOWEST
        // occupied index — scanned upward from the entry edge, first occupant wins.
        // This replaces the former escape-edge captor (highest occupied index). If the
        // City has no villains, the Mastermind captures the bystander instead. The
        // bystander is NOT routed to villainDeck.discard.
        let captorCardId = G.mastermind.baseCardId;
        for (let cityIndex = 0; cityIndex < G.city.length; cityIndex++) {
            const occupant = G.city[cityIndex];
            if (occupant !== null && occupant !== undefined) {
                captorCardId = occupant;
                break;
            }
        }
        const existingAttached = G.attachedBystanders[captorCardId] ?? [];
        G.attachedBystanders = {
            ...G.attachedBystanders,
            [captorCardId]: [...existingAttached, cardId],
        };
        // why: when the mastermind is the captor (city is empty), also push to
        // G.mastermind.attachedBystanders so the UI projection surfaces it on
        // the mastermind tile. G.attachedBystanders is the authoritative store
        // for all bystander attachments; G.mastermind.attachedBystanders is the
        // projection surface the UI reads (D-12805 Interpretation B).
        if (captorCardId === G.mastermind.baseCardId) {
            const existing = G.mastermind.attachedBystanders ?? [];
            G.mastermind = {
                ...G.mastermind,
                attachedBystanders: [...existing, cardId],
            };
        }
        pushLog(G, `${formatCardRef(G.cardDisplayData, cardId)} revealed and captured by ${formatCardRef(G.cardDisplayData, captorCardId)}.`);
        // why: WP-602 / D-24412 — emit a bystanderRevealed notableEvent as the final
        // step of the capture branch (ADDITIVE to the log line above, not a
        // replacement), so the arena-client NotableEventOverlay announces a revealed
        // Bystander like it does a Scheme Twist / Master Strike — the only villain-deck
        // reveal outcome that previously raised no overlay. Unconditional push (setup
        // guarantees G.notableEvents), mirroring the ambushResolved site in this file.
        // Names resolve HERE via G.cardDisplayData (the composer stays pure); defensive
        // raw-ext_id fallback when display data is absent (legacy test states may omit it).
        const bystanderDisplay = G.cardDisplayData?.[cardId];
        const bystanderName = bystanderDisplay && typeof bystanderDisplay.name === 'string' && bystanderDisplay.name.length > 0
            ? bystanderDisplay.name
            : cardId;
        const captorDisplay = G.cardDisplayData?.[captorCardId];
        const captorName = captorDisplay && typeof captorDisplay.name === 'string' && captorDisplay.name.length > 0
            ? captorDisplay.name
            : captorCardId;
        G.notableEvents.push({
            type: 'bystanderRevealed',
            revealedCardId: cardId,
            captorCardId,
            narrative: composeBystanderRevealedNarrative(bystanderName, captorName),
        });
    }
    else if (cardType === 'scheme-twist') {
        // why: scheme-twist cards route to G.scheme.twistPile (not discard)
        // so the game tracks resolved twists for UI projection and future
        // scheme-loss evaluation
        G.scheme.twistPile = [...G.scheme.twistPile, cardId];
    }
    else if (cardType === 'mastermind-strike') {
        // why: mastermind-strike cards route to G.mastermind.strikePile (not
        // discard) so the game tracks resolved strikes for UI projection
        G.mastermind.strikePile = [...G.mastermind.strikePile, cardId];
        // why: WP-743 / D-24566 — record the Master Strike for the Grief and Spring the
        // Trap this-turn gates, gated on either condition being read in this match (games
        // without them stay byte-unchanged, the D-24467 posture). Their readers run at the
        // next onMove resolution or a later play, so setting this after the strike's
        // effects have applied is safe.
        if (matchReadsConditionType(G, MASTER_STRIKE_THIS_TURN_CONDITION_TYPE) ||
            matchReadsConditionType(G, MASTER_STRIKE_OR_AMBUSH_THIS_TURN_CONDITION_TYPE)) {
            G.masterStrikePlayedThisTurn = true;
        }
    }
}
/**
 * Resolves one Villain escaping the City: the escape counter + Escaped Villains pile,
 * bystander carry-away, the rulebook HQ KO + Bystander discard the escape owes (recorded
 * here, opened after the move by the turn.onMove escape opener — D-24656), card-text
 * Escape effects, captured heroes to the Escape Pile (D-24657), the escape→Scheme-Twist
 * branch (Mystique) and the escaped-pile resource-loss check. No escape gives a generic
 * Wound (D-24656 supersedes the MVP placeholder of D-1702 / D-24439).
 *
 * // why: WP-757 / D-24587 — extracted mechanically from performVillainReveal's escape
 * branch (the reveal path calls it with identical behavior) so every push into the
 * City — a villain-deck reveal, or a Haunt exorcise that ignores Ambush
 * (enterCityIgnoringAmbush) — resolves an escape the same way.
 *
 * @param G - The game state to mutate.
 * @param context - Narrow reveal context (random + ctx.currentPlayer).
 * @param implementationMap - Handler map used by executeRuleHooks (escape→Scheme-Twist).
 * @param escapedCardId - The card pushed out of City space 4.
 */
export function resolveVillainEscape(G, context, implementationMap, escapedCardId) {
    const ctx = context.ctx;
    // why: ENDGAME_CONDITIONS.ESCAPED_VILLAINS is the canonical counter key
    // for escape tracking. evaluateEndgame reads this counter to determine
    // scheme-wins loss condition.
    const currentEscaped = G.counters[ENDGAME_CONDITIONS.ESCAPED_VILLAINS] ?? 0;
    G.counters[ENDGAME_CONDITIONS.ESCAPED_VILLAINS] = currentEscaped + 1;
    // why: escaped card pushed to G.escapedPile only when non-null (null
    // means no card was displaced). Counter increments regardless — the
    // counter tracks escape events, the pile tracks card identity.
    G.escapedPile = [...G.escapedPile, escapedCardId];
    pushLog(G, `Villain ${formatCardRef(G.cardDisplayData, escapedCardId)} escaped from the city.`);
    // why: D-24314 — an escaping villain CARRIES its captured bystanders into
    // the Escaped Villains pile (G.escapedPile), not back to the shared supply.
    // This is faithful to Universal Rules v23 escape handling and makes the
    // bystanders countable for resource-loss schemes (e.g. Midtown Bank
    // Robbery: "8 Bystanders carried away by escaping Villains"). The mapping
    // entry is still cleared (no leak); G.piles.bystanders is NOT touched here.
    const escapedPileBeforeBystanders = G.escapedPile.length;
    const escapeBystanderResult = carryEscapedBystandersToPile(escapedCardId, G.attachedBystanders, G.escapedPile);
    G.attachedBystanders = escapeBystanderResult.attachedBystanders;
    G.escapedPile = escapeBystanderResult.escapedPile;
    const hasCarriedBystanders = escapeBystanderResult.escapedPile.length > escapedPileBeforeBystanders;
    if (hasCarriedBystanders) {
        pushLog(G, `Bystanders from escaped villain ${formatCardRef(G.cardDisplayData, escapedCardId)} carried into the Escaped Villains pile.`);
    }
    // why: WP-793 / D-24656 — rules v23 L556–L570 owe, in order, (1) an HQ KO of a Hero
    // costing 6 or less (the current player chooses) and (2) a discard by every player if
    // Bystanders were carried away, before (3) the Escape effect. Steps 1–2 need player
    // choices this events-less site cannot park, so they are recorded here and resolve
    // AFTER the move, opened by the play-phase turn.onMove escape opener (the accepted
    // deviation, D-24656 point 4). Step 3 stays inline below so the escaping Villain's
    // Escape effect still resolves before the next Villain's Ambush (rules v23 L573).
    enqueueEscapeProcedure(G, escapedCardId, ctx.currentPlayer, hasCarriedBystanders);
    // why: card-specific Escape:/Overrun: effects fire AFTER
    // carryEscapedBystandersToPile per D-18603 — a captureBystander effect
    // reached via an Escape: marker attaches to the escaped card now in
    // G.escapedPile (post-carry), not the still-attached pre-carry
    // state. They are step 3 of the rulebook procedure (D-24656); the
    // owed HQ KO + Bystander discard recorded above resolve after the
    // move, and no escape adds a generic Wound. Overrun: is a v1 synonym of Escape:
    // (D-18602) — both prefixes resolve to onEscape at parse time, so this
    // single fire site covers both. Henchman escapes safely no-op here
    // (per-card hook lookup misses; D-18507-class filter). Per WP-191
    // (D-18704..D-18708), escapedCardId is the zone-instance
    // ext_id the per-card hook lookup expects, so villain onEscape effects
    // now fire end-to-end on real cards (D-18508 CLOSED).
    const appliedEscapeResults = executeVillainAbilities(G, ctx, escapedCardId, 'onEscape', 
    // why: WP-478 / D-24285 — thread the reveal move's shuffle source so a future
    // Escape-timed scry could reshuffle a short deck (no such marker today; kept
    // uniform with the Fight fire site). Wrapped as a ShuffleProvider `{ random }`
    // from the RevealContext's random API (the bare `ctx` carries only currentPlayer).
    { random: context.random }, 
    // why: WP-489 / D-24295 — the Escape fire site has no fought City space, so
    // cityIndex is undefined; the location gate FAILS CLOSED, so any
    // requireCitySpaces effect at Escape never fires (none of Tier B's cards escape).
    undefined);
    // why: WP-316 — Escape is LOG-ONLY: narrate the Escape: effect targets
    // into G.messages (hash-excluded, D-24081) but add NO escapeResolved (or
    // any) notableEvent. G.notableEvents is hashed + projected to the
    // arena-client, so a new event would re-pin the sentinel finalStateHash.
    // Length-guarded: no line when no effect applied. Names resolve at the
    // fire site via G.cardDisplayData (the composer stays pure).
    if (appliedEscapeResults.length > 0) {
        const resolvedEscapeResults = resolveEffectResultNames(G, appliedEscapeResults);
        pushLog(G, `Escape effect: ${composeEffectResultLogLine(resolvedEscapeResults)}.`);
    }
    // why: D-24657 — captured heroes stay in the Escape Pile with their captor (rules v23)
    moveAttachedHeroesToEscapedPile(G, escapedCardId);
    // why: WP-481 / D-24287 — Mystique's "Escape: … becomes a Scheme Twist that
    // takes effect immediately." The executor's become-scheme-twist handler is a
    // no-op (it cannot reach the rule pipeline); the actual twist fires HERE, where
    // the hookRegistry + implementationMap + RevealContext are in scope. Run the
    // SAME two-call pattern the reveal path uses for a revealed scheme-twist card
    // (Step 5/6 above): schemeTwistCount increments, the active scheme's twist
    // resolver runs (D-24775: count first), and the loss threshold is checked. The escaped card stays in the
    // escaped pile — resolvers use the cardId only to stamp a schemeTwistResolved
    // notableEvent, never to route a card. "Takes effect immediately" → after the
    // escape's own in-move consequences (bystander carry-away / escape effects / captured heroes to the
    // Escape Pile); the owed HQ KO + Bystander discard resolve after the move (D-24656).
    if (villainCardEscapeTriggersSchemeTwist(G, escapedCardId)) {
        pushLog(G, `Escape effect: ${formatCardRef(G.cardDisplayData, escapedCardId)} becomes a Scheme Twist that takes effect immediately.`);
        const escapeSchemeTwistEffects = executeRuleHooks(G, context, 'onSchemeTwistRevealed', { cardId: escapedCardId }, G.hookRegistry, implementationMap);
        applyRuleEffects(G, context, escapeSchemeTwistEffects);
        // why: WP-488 / D-24294 — the become-scheme-twist executor handler is a deliberate
        // no-op (D-24287); the REAL Scheme Twist just fired HERE via the rule pipeline. Emit
        // a `secondary-site` trace so the Mystique case is answerable ("the executor was a
        // no-op; the twist fired at the escape site"), distinct from the executor's `no-op`
        // trace for the same card. `handler` names the rule trigger that ran; `turn` is the
        // RevealContext's framework turn (D-24704), the same value the villain executor's
        // records on this reveal path read.
        recordEffectTrace(G, {
            cardId: escapedCardId,
            scope: 'villain',
            timing: 'onEscape',
            effect: 'become-scheme-twist',
            handler: 'onSchemeTwistRevealed',
            status: 'secondary-site',
            fireSite: 'escape-scheme-twist',
            params: {},
            turn: readRevealContextTurn(context),
        });
    }
    // why: D-24315 — evaluate the active scheme's escaped-pile resource-loss
    // condition at the END of the escape branch, after every escape
    // consequence has settled (bystander carry-away, card-text Escape:
    // effects, captured heroes to the Escape Pile, and the Mystique
    // escape→scheme-twist path). Every escape path — including the Secret
    // Invasion Skrull push (D-24656) — grows G.escapedPile here, so the count
    // reflects the full escape. No-op for schemes with no resourceLossCondition.
    applyEscapedPileResourceLoss(G);
    // why: WP-832 / D-24765 — Change the Outcome of WWII: an escape conquers the current country's
    // capital AT the escape, here on the one path every escape takes, so the live game, the sim /
    // PAR / fixture loops and replay all count it the same way, and a third capital latches before
    // the escape-procedure opener runs. No-op until a country has been invaded.
    settleWwiiConquest(G);
}
/**
 * Plays (reveals + resolves) the top `count` cards of the Villain Deck via
 * `performVillainReveal`, stopping early when the deck and discard are both empty
 * (WP-542 / D-24351).
 *
 * The shared reveal loop behind the recursive villain-deck-play primitive — the
 * `chainedReveals` scheme resolver's loop, extracted here so both fire sites use
 * it: The Leader's onAmbush (in this file, count 1) and Endless Armies of HYDRA's
 * onFight (in fightVillain.ts, count 2). Each play resolves exactly as a normal
 * villain-deck reveal (villain → City, henchman / bystander / scheme-twist /
 * master-strike as usual), recursing through the rule pipeline: a played card that
 * itself plays more villain-deck cards, triggers a scheme twist, or fires a
 * master-strike resolves in turn. Recursion terminates because each
 * `performVillainReveal` consumes a deck card (the Villain Deck does not reshuffle,
 * D-24160) and the both-empty guard stops the loop at exhaustion.
 *
 * @param G - The game state to mutate.
 * @param context - Narrow reveal context (random + ctx.currentPlayer) threaded into
 *   `performVillainReveal`.
 * @param implementationMap - Handler map threaded into `performVillainReveal`.
 * @param count - How many top villain-deck cards to play (>= 1).
 */
export function playTopVillainDeckCards(G, context, implementationMap, count) {
    for (let playIndex = 0; playIndex < count; playIndex++) {
        // why: WP-542 / D-24351 — the both-empty guard: never call
        // performVillainReveal past deck+discard exhaustion (recursion terminates on
        // exhaustion). Mirrors the chainedReveals scheme resolver's empty-deck break.
        if (G.villainDeck.deck.length === 0 &&
            G.villainDeck.discard.length === 0) {
            pushLog(G, `Villain deck exhausted after playing ${playIndex} of ${count} card(s).`);
            break;
        }
        performVillainReveal(G, context, implementationMap);
    }
}
//# sourceMappingURL=villainDeck.reveal.js.map